/**
 * PARTIDAS — la calculadora de capa (escalón E4b, 2026-08-22).
 *
 * Convierte lo que la paleta capturó en un recinto (`cotizaciones.objetos`, estado
 * `propuesto`) en renglones cotizables de la habitación de ese recinto, con `rubro`
 * y `recinto` para poder cotizar por partes (plan §5). Produce VARIAS partidas, no
 * un renglón «Instalación eléctrica — Recámara» que esconda todo:
 *
 *   · una por tipo de salida propuesta, con el SKU del catálogo (`SAL-CTO`,
 *     `SAL-CTO-P`, `SAL-ILU`, `SAL-ESC`) y la cantidad contada;
 *   · una por aparato que pide circuito dedicado (`CTO-DED`);
 *   · UNA de alimentación del recinto —el tramo del tablero al recinto por plafón,
 *     medido en el plano por `trayectos.js`— como producto especial (`def`) con su
 *     cable y tubo, su mano de obra, su explicación y sus supuestos.
 *
 * Es idempotente: las partidas que nacen aquí llevan `origen: 'paleta'`, y volver a
 * calcular sustituye solo ésas; lo que el vendedor agregó a mano del catálogo se
 * queda. Los hallazgos NO producen partidas aquí: alimentan el Paquete Raíz en la
 * pantalla de cierre (E5), con el ingeniero de por medio.
 */

import { cableDelRecinto, ALTURAS_BASE } from './trayectos.js'
import { factorPorMinutos, RENDIMIENTOS_BASE } from './levantamiento.js'

const ahora = () => new Date().toISOString()
export const RUBROS_CON_MOTOR = ['riego', 'camaras', 'wifi']

/** Los objetos propuestos de un recinto, con SKU, agrupados por SKU (y el rubro de cada uno). */
function propuestosPorSku(objetos, recintoId) {
  const porSku = new Map()
  for (const o of Object.values(objetos || {})) {
    if (o.recinto !== recintoId || o.estado !== 'propuesto' || !o.sku || o.tipo === 'hallazgo') continue
    /* Riego, Cámaras y Wi-Fi se colocan en el plano pero su partida la produce SU motor
       (cobertura, zonas, DORI): aquí no se convierten en renglones (E7-lite, 2026-08-23). */
    if (RUBROS_CON_MOTOR.includes(o.rubro)) continue
    const previo = porSku.get(o.sku) || { q: 0, rubro: o.rubro || 'electrico' }
    porSku.set(o.sku, { q: previo.q + (o.q || 0), rubro: previo.rubro })
  }
  return porSku
}

/**
 * Partidas de un recinto a partir de sus objetos propuestos.
 * @returns { items: [...], alimentacion: { explicacion, supuestos } | null }
 */
export function partidasDeRecinto({ objetos, plano, recintoId, rendimientos = RENDIMIENTOS_BASE, minutosDia = 480, alturas = ALTURAS_BASE }) {
  const rd = { ...RENDIMIENTOS_BASE, ...rendimientos }
  const porSku = propuestosPorSku(objetos, recintoId)
  const items = []
  for (const [sku, { q, rubro }] of porSku) {
    if (q > 0) items.push({ sku, q, e1: true, e2: false, e3: false, fotos: [], origen: 'paleta', rubro, recinto: recintoId })
  }
  /* La alimentación (cable medido desde el tablero) solo existe si hay salidas
     eléctricas propuestas; lo de smart, iluminación o taller no la pide (F3). */
  if (!items.some(it => it.rubro === 'electrico')) return { items, alimentacion: null }

  const mios = Object.values(objetos || {}).filter(o => o.recinto === recintoId)
  const cable = cableDelRecinto(plano, recintoId, mios, alturas)
  const nombreRecinto = plano?.recintos?.[recintoId]?.nombre || recintoId
  const minutos = Math.round(cable.metrosTubo * rd.minutoCanalizacion + rd.minutoCircuito)
  const def = {
    sku: `ALIM-${recintoId}`,
    nombre: `Alimentación eléctrica — ${nombreRecinto}`,
    cat: 'tab', tIns: minutos, tCfg: 0, precio: 0,
    factor: factorPorMinutos(minutos, minutosDia),
    comp: [{ sku: 'CBL-12', c: cable.metrosCable }, { sku: 'TUBO-13', c: cable.metrosTubo }],
    especial: true,
    disenio: { tipo: 'alimentacion', recinto: recintoId, metrosPlafon: cable.metrosPlafon, supuesto: cable.supuesto, bajadas: cable.bajadas, calculadoEn: ahora() },
    resumen: [cable.explicacion],
    supuestos: cable.supuestos,
    pendientes: []
  }
  items.push({ sku: def.sku, q: 1, e1: true, e2: false, e3: false, fotos: [], def, origen: 'paleta', rubro: 'electrico', recinto: recintoId })
  return { items, alimentacion: { explicacion: cable.explicacion, supuestos: cable.supuestos } }
}

/** Sustituye en la habitación las partidas de origen `paleta` por las nuevas. No muta. */
export function aplicarPartidas(habitacion, nuevas) {
  const manuales = (habitacion.items || []).filter(it => it.origen !== 'paleta')
  return { ...habitacion, items: [...manuales, ...nuevas] }
}
